import { useQuery } from '@tanstack/react-query';
import { api } from '../client';
import type { ApiFormattedUnit, ApiUnit } from '../types';

interface UseUnitsOptions {
    projectId?: string;
    areaId?: string;
    type?: string;
    page?: number;
    limit?: number;
    enabled?: boolean;
    occupied?: boolean;
    ids?: string[];
}

import i18n from '../../../i18n';

// ... (imports)

export function useUnits(options: UseUnitsOptions = {}) {
    const { projectId, areaId, type, page = 1, limit = 20, enabled = true, occupied, ids } = options;

    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (projectId) params.set('projectId', projectId);
    if (areaId) params.set('areaId', areaId);
    if (type) params.set('type', type);
    if (ids && ids.length > 0) params.set('ids', ids.join(','));

    return useQuery({
        queryKey: ['units', { projectId, areaId, type, page, limit, ids, locale: i18n.language }],
        queryFn: async () => {
            const res = await api.get<ApiFormattedUnit[]>(`/api/units?${params.toString()}`);
            return res.data || [];
        },
        enabled,
    });
}

export function useProjectUnits(projectSlug: string | undefined) {
    return useQuery({
        queryKey: ['units', 'project', projectSlug, i18n.language],
        queryFn: async () => {
            const res = await api.get<ApiUnit[]>(`/api/units/project/${projectSlug}`);
            return res;
        },
        enabled: !!projectSlug,
    });
}

export function useUnit(slug: string | undefined) {
    return useQuery({
        queryKey: ['unit', slug, i18n.language],
        queryFn: async () => {
            const res = await api.get<ApiFormattedUnit>(`/api/units/${slug}`, {
                params: { locale: i18n.language }
            });
            return res.data!;
        },
        enabled: !!slug,
    });
}

export function useSuggestedUnits(slug: string | undefined) {
    return useQuery({
        queryKey: ['unit-suggested', slug, i18n.language],
        queryFn: async () => {
            const res = await api.get<ApiFormattedUnit[]>(`/api/units/${slug}/suggested`);
            return res.data!;
        },
        enabled: !!slug,
    });
}
